import { Controller } from "@hotwired/stimulus"
import { track } from "../lib/mixpanel"

// The hover card of an action item chat's email line (Phelipe 2026-09-29):
// "Emailed to 14 people" -> a table of each person, how it went (right away,
// in their Summary, not sent because their emails are off), when they last
// opened the email and when they last clicked to view the item. One floating
// card for the whole page, placed by the line and kept on screen, so the chat's
// scrolling box never cuts it off. The words come from the server
// (ActionItemEmailActivity); times show in the viewer's own clock.

let card = null
let hideTimer = null
let owner = null

// The card beside the chat: this far from it, and only when that side has room.
const SIDE_GAP = 16
const SIDE_MIN_WIDTH = 420
const SIDE_MAX_WIDTH = 540
// Long enough to move the mouse from the line over to the card (to scroll a long list).
const HIDE_DELAY = 450

const esc = (value) => String(value ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]))

const when = (iso) => {
  if (!iso) return ""
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ""
  return date.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
}

function scheduleHide() {
  clearTimeout(hideTimer)
  hideTimer = setTimeout(() => {
    if (card) card.classList.add("hidden")
    owner = null
  }, HIDE_DELAY)
}

function cardElement() {
  if (card && document.body.contains(card)) return card
  card = document.createElement("div")
  card.className = "hidden fixed z-[2147483006] w-[540px] max-w-[calc(100vw-32px)] rounded-lg border border-gray-200 bg-white p-3 text-[13px] not-italic text-gray-700 shadow-lg"
  card.setAttribute("role", "tooltip")
  card.addEventListener("mouseenter", () => clearTimeout(hideTimer))
  card.addEventListener("mouseleave", scheduleHide)
  document.body.appendChild(card)
  return card
}

export default class extends Controller {
  static values = { batch: Object }

  show() {
    clearTimeout(hideTimer)
    const el = cardElement()
    el.innerHTML = this._html()
    el.classList.remove("hidden")
    this._place(el)
    owner = this
    if (!this._seen) {
      this._seen = true
      track("Action Item Email Activity Viewed", { kind: this.batchValue.kind, people: (this.batchValue.rows || []).length })
    }
  }

  hide() {
    scheduleHide()
  }

  disconnect() {
    if (owner === this && card) {
      card.classList.add("hidden")
      owner = null
    }
  }

  // Beside the chat, on its left, level with the line (Phelipe 2026-09-29: it
  // mustn't cover the messages under it) - the chat marks its box with
  // data-email-card-boundary. Without room there (a narrow screen), under the
  // line, or above it when there's no room below; always on screen.
  _place(el) {
    const rect = this.element.getBoundingClientRect()
    const boundary = this.element.closest("[data-email-card-boundary]")
    const room = boundary ? boundary.getBoundingClientRect().left - SIDE_GAP - 8 : 0
    if (room >= SIDE_MIN_WIDTH) {
      el.style.width = `${Math.min(SIDE_MAX_WIDTH, room)}px`
      const sideTop = Math.max(8, Math.min(rect.top - 8, window.innerHeight - el.offsetHeight - 8))
      el.style.left = `${boundary.getBoundingClientRect().left - SIDE_GAP - el.offsetWidth}px`
      el.style.top = `${sideTop}px`
      return
    }
    el.style.width = ""
    const width = el.offsetWidth
    const height = el.offsetHeight
    let top = rect.bottom + 6
    if (top + height > window.innerHeight - 8 && rect.top - height - 6 > 8) top = rect.top - height - 6
    top = Math.max(8, Math.min(top, window.innerHeight - height - 8))
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8))
    el.style.top = `${top}px`
    el.style.left = `${left}px`
  }

  _html() {
    const batch = this.batchValue || {}
    const labels = batch.labels || {}
    const dash = '<span class="text-gray-300">—</span>'
    const sentTone = (kind) => (kind === "summary" ? "text-amber-600" : kind === "off" ? "italic text-gray-400" : "text-gray-600")
    const rows = (batch.rows || []).map((row, i) => {
      const opened = row.opened_at ? esc(when(row.opened_at)) : (row.sent_kind === "off" ? dash : `<span class="italic text-gray-400">${esc(labels.not_yet)}</span>`)
      const clicked = row.clicked_at ? esc(when(row.clicked_at)) : dash
      return `<tr class="${i % 2 ? "bg-gray-50" : ""}">
        <td class="py-1 pl-1.5 pr-2 text-gray-800">${esc(row.name)}</td>
        <td class="whitespace-nowrap py-1 pr-2 ${sentTone(row.sent_kind)}">${esc(row.sent)}</td>
        <td class="whitespace-nowrap py-1 pr-2">${opened}</td>
        <td class="whitespace-nowrap py-1 pr-1.5">${clicked}</td>
      </tr>`
    }).join("")

    return `<div class="font-semibold text-gray-900">${esc(batch.title)} · ${esc(when(batch.at))}</div>
      <div class="mb-2 text-gray-500">${esc(batch.stats)}</div>
      <div class="max-h-64 overflow-y-auto">
        <table class="w-full border-collapse text-left">
          <thead class="sticky top-0 bg-white">
            <tr class="text-gray-400">
              <th class="pb-1 pl-1.5 pr-2 font-semibold">${esc(labels.person)}</th>
              <th class="pb-1 pr-2 font-semibold">${esc(labels.sent)}</th>
              <th class="pb-1 pr-2 font-semibold">${esc(labels.opened)}</th>
              <th class="pb-1 pr-1.5 font-semibold">${esc(labels.clicked)}</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
      </div>`
  }
}
